import React, { useMemo, useState } from "react";
import { Droplets, Eraser, ArrowLeftRight, Beaker } from "lucide-react";
import { SPECIES, computeHardness, UNITS, convert, classAS, classWHO } from "@/lib/hardness";
import { fmt } from "@/lib/storage";

const PRESETS = [
  { label: "Örnek: 80 mg Ca²⁺ + 24 mg Mg²⁺", v: { Ca: "80", Mg: "24" } },
  { label: "Örnek: 324 mg Ca(HCO₃)₂ + 120 mg MgSO₄", v: { CaHCO3: "324", MgSO4: "120" } },
  { label: "Örnek: 140 mg CaO", v: { CaO: "140" } },
];

const SCALE = [
  [4, "Çok yumuşak"], [8, "Yumuşak"], [12, "Biraz sert"], [18, "Oldukça sert"], [30, "Sert"], [40, "Çok sert"],
];

function Result({ label, value, unit, testid }) {
  return (
    <div className="res-tile" data-testid={testid}>
      <span className="res-label">{label}</span>
      <span className="res-value">{value}</span>
      <span className="res-unit">{unit}</span>
    </div>
  );
}

export default function HardnessCalculator() {
  const [inputs, setInputs] = useState({ Ca: "", Mg: "" });
  const [showAll, setShowAll] = useState(false);
  const [cv, setCv] = useState("20");
  const [cu, setCu] = useState("AS");

  const r = useMemo(() => computeHardness(inputs), [inputs]);
  const conv = useMemo(() => convert(cv, cu), [cv, cu]);
  const hasData = r.steps.length > 0;
  const list = showAll ? SPECIES : SPECIES.slice(0, 4);
  const pos = Math.min(r.as / 40, 1) * 100;

  return (
    <div>
      <div className="section-head">
        <span className="eyebrow">KİMÖ-453 · Etkileşimli Araç</span>
        <h1 className="page-title">Canlı Su Sertliği Hesaplayıcı ve Dönüştürücü</h1>
        <p className="page-sub">1 litre sudaki miktarları mg cinsinden gir; AS°, FS°, İS°, ppm ve sertlik sınıfı adım adım anında hesaplansın.</p>
      </div>

      <div className="calc-grid">
        <div className="panel">
          <div className="panel-head"><Beaker size={16} /> Su numunesi (mg / 1 L)</div>
          <div className="inputs">
            {list.map((s) => (
              <label key={s.key} className="inp">
                <span className="inp-label">{s.label} <em>M = {s.M}</em>{s.kind !== "genel" && <i className={`kind ${s.kind}`}>{s.kind === "gecici" ? "geçici" : "kalıcı"}</i>}</span>
                <input
                  type="number"
                  min="0"
                  inputMode="decimal"
                  placeholder="0"
                  value={inputs[s.key] ?? ""}
                  onChange={(e) => setInputs({ ...inputs, [s.key]: e.target.value })}
                  data-testid={`hc-input-${s.key}`}
                />
              </label>
            ))}
          </div>
          <div className="panel-actions">
            <button className="btn btn-ghost" onClick={() => setShowAll((x) => !x)} data-testid="hc-toggle-more">
              {showAll ? "Sadece temel girdiler" : "+ MgO, MgCO₃, bikarbonat, sülfat, klörür"}
            </button>
            <button className="btn btn-ghost" onClick={() => setInputs({})} data-testid="hc-clear"><Eraser size={15} /> Temizle</button>
          </div>
          <div className="presets">
            {PRESETS.map((p, i) => (
              <button key={i} className="chip" onClick={() => { setInputs(p.v); if (Object.keys(p.v).some((k) => !["CaO", "CaCO3", "Ca", "Mg"].includes(k))) setShowAll(true); }} data-testid={`hc-preset-${i}`}>{p.label}</button>
            ))}
          </div>
        </div>

        <div className="panel panel-result" data-testid="hc-results">
          <div className="panel-head"><Droplets size={16} /> Sonuç</div>
          {!hasData ? (
            <div className="empty small"><p>Hesaplama için en az bir değer gir ya da bir örnek seç.</p></div>
          ) : (
            <>
              <div className="res-grid">
                <Result label="Alman" value={fmt(r.as)} unit="AS°" testid="hc-res-as" />
                <Result label="Fransız" value={fmt(r.fs)} unit="FS°" testid="hc-res-fs" />
                <Result label="İngiliz" value={fmt(r.is)} unit="İS°" testid="hc-res-is" />
                <Result label="ppm" value={fmt(r.ppm)} unit="mg CaCO₃/L" testid="hc-res-ppm" />
              </div>
              <div className="scale">
                <div className="scale-bar">
                  {SCALE.map(([max, l], i) => <span key={i} style={{ flex: max - (i ? SCALE[i - 1][0] : 0) }} title={l} />)}
                  <i className="scale-pin" style={{ left: `${pos}%` }} />
                </div>
                <div className="scale-labels"><span>0</span><span>4</span><span>8</span><span>12</span><span>18</span><span>30</span><span>40+ AS°</span></div>
              </div>
              <div className="class-row">
                <div className="class-pill" data-testid="hc-class-as">AS° sınıfı: <strong>{r.classAS}</strong></div>
                <div className="class-pill" data-testid="hc-class-who">WHO sınıfı: <strong>{r.classWHO}</strong></div>
              </div>
              {(r.gecici || r.kalici) && (
                <div className="split-row">
                  {r.gecici && <div className="split gecici">Geçici (bikarbonat): <strong>{fmt(r.gecici.fs)} FS° · {fmt(r.gecici.as)} AS°</strong></div>}
                  {r.kalici && <div className="split kalici">Kalıcı (sülfat/klörür): <strong>{fmt(r.kalici.fs)} FS° · {fmt(r.kalici.as)} AS°</strong></div>}
                </div>
              )}
              <div className="steps">
                <div className="steps-title">Adım adım çözüm</div>
                <ol>
                  {r.steps.map((s, i) => <li key={i}><span className="step-n">{i + 1}</span><span>{s.text}</span></li>)}
                  <li><span className="step-n">{r.steps.length + 1}</span><span>Toplam = {fmt(r.totalMmol, 3)} mmol → CaO eş. = {fmt(r.totalMmol, 3)} × 56 = {fmt(r.cao)} mg → <b>AS° = {fmt(r.cao)} ÷ 10 = {fmt(r.as)}</b></span></li>
                  <li><span className="step-n">{r.steps.length + 2}</span><span>CaCO₃ eş. = {fmt(r.totalMmol, 3)} × 100 = {fmt(r.caco3)} mg → <b>FS° = {fmt(r.caco3)} ÷ 10 = {fmt(r.fs)}</b></span></li>
                  <li><span className="step-n">{r.steps.length + 3}</span><span><b>İS° = {fmt(r.caco3)} ÷ 14,3 = {fmt(r.is)}</b> · <b>ppm = {fmt(r.ppm)}</b></span></li>
                  <li><span className="step-n">{r.steps.length + 4}</span><span>Sınıf: {fmt(r.as)} AS° → {r.classAS}; {fmt(r.caco3)} mg/L → WHO: {r.classWHO}{r.gecici || r.kalici ? " · Toplam sertlik = geçici + kalıcı" : ""}</span></li>
                </ol>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="panel converter" data-testid="hc-converter">
        <div className="panel-head"><ArrowLeftRight size={16} /> Birim Dönüştürücü</div>
        <div className="conv-row">
          <input type="number" value={cv} onChange={(e) => setCv(e.target.value)} className="conv-input" data-testid="conv-value" />
          <select className="select" value={cu} onChange={(e) => setCu(e.target.value)} data-testid="conv-unit">
            {UNITS.map((u) => <option key={u.key} value={u.key}>{u.label}</option>)}
          </select>
          <span className="conv-eq">=</span>
          {conv ? (
            <div className="conv-out" data-testid="conv-output">
              <span><b>{fmt(conv.as)}</b> AS°</span>
              <span><b>{fmt(conv.fs)}</b> FS°</span>
              <span><b>{fmt(conv.is)}</b> İS°</span>
              <span><b>{fmt(conv.ppm)}</b> ppm</span>
              <span className="muted">{classAS(conv.as)} · WHO: {classWHO(conv.caco3)}</span>
            </div>
          ) : <span className="muted">Geçerli bir sayı gir.</span>}
        </div>
        <p className="conv-note">1 AS° = 1,79 FS° · 1 FS° = 0,56 AS° · 1 İS° = 1,43 FS° = 0,8 AS° · 1 ppm = 1 mg CaCO₃/L</p>
      </div>
    </div>
  );
}
